import { useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { PencilIcon, PlusIcon, Trash2Icon, Undo2Icon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { ConfirmationDialog } from 'components/reusable/ConfirmationDialog'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'components/ui/sheet'
import { Badge, Button, IconButton, Table } from 'slab'
import {
  getGetPartnersPartnerIdPartnerAdminRelationshipsQueryKey,
  useGetPartnersPartnerIdPartnerAdminRelationships,
  usePostPartnersPartnerIdPartnerAdminRelationshipsBatch,
} from 'utils/web_api'
import type {
  PartnersPartnerAdmin,
  PostPartnersPartnerIdPartnerAdminRelationshipsBatch,
  WebAPIPartnersEntitiesPartnerAdminRelationship,
} from 'utils/web_api.schemas'

import { AddPartnerAdminRelationshipRow } from './AddPartnerAdminRelationshipRow'
import { PartnerAdminRelationshipRoleSelector } from './PartnerAdminRelationshipRoleSelector'

interface PartnerAdminRelationshipSheetProps {
  employerId: string
  partnerId: string
  partnerDisplayName: string
  isOpen: boolean
  onClose: () => void
  onUpdate?: () => void
}

interface PendingAdd {
  tempId: string
  partnerAdmin: PartnersPartnerAdmin
  role: string
}

const ROLE_BADGE_COLORS = {
  assignee: 'blue',
  editor: 'green',
  viewer: 'gray',
} as const

const COLUMN_WIDTHS = {
  name: 320,
  role: 160,
  date: 128,
} as const

const formatPartnerAdminName = (admin: PartnersPartnerAdmin) => {
  const roles = admin.roles?.length
    ? ` (${admin.roles.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')})`
    : ''
  return `${admin.email}${roles}`
}

const capitalizeRole = (role: string) => role.charAt(0).toUpperCase() + role.slice(1)

const RolePermissionsInfo = () => (
  <div className='mb-4 text-sm text-gray-600'>
    <p className='font-medium mb-2'>Role Permissions:</p>
    <ul className='space-y-1'>
      <li><strong>Assignee:</strong> Can edit quotes and invite other brokers (only one assignee per employer)</li>
      <li><strong>Editor:</strong> Can edit prospects and quotes</li>
      <li><strong>Viewer:</strong> Can view prospects, quotes, and clients</li>
    </ul>
  </div>
)

interface EmptyStateProps {
  availableAdminsCount: number
  onAddClick: () => void
  isSaving: boolean
}

const EmptyState = ({ availableAdminsCount, onAddClick, isSaving }: EmptyStateProps) => (
  <Table.Row>
    <td colSpan={4} className='px-4 py-8 text-center'>
      <div className='text-gray-500 mb-4'>
        No partner admins assigned yet
      </div>
      {availableAdminsCount > 0 && (
        <Button
          variant='secondary'
          size='small'
          onClick={onAddClick}
          leftIcon={PlusIcon}
          disabled={isSaving}
        >
          Add Partner Admin
        </Button>
      )}
    </td>
  </Table.Row>
)

interface ExistingRelationshipRowProps {
  relationship: WebAPIPartnersEntitiesPartnerAdminRelationship
  currentRole: string
  isPendingUpdate: boolean
  isEditing: boolean
  hasAssignee: boolean
  isSaving: boolean
  onRoleChange: (relationshipId: string, newRole: string) => void
  onDelete: (relationshipId: string) => void
  onEditStart: (relationshipId: string) => void
  onEditEnd: () => void
}

const ExistingRelationshipRow = ({
  relationship,
  currentRole,
  isPendingUpdate,
  isEditing,
  hasAssignee,
  isSaving,
  onRoleChange,
  onDelete,
  onEditStart,
  onEditEnd,
}: ExistingRelationshipRowProps) => (
  <Table.Row key={relationship.id}>
    <Table.Cell width={COLUMN_WIDTHS.name}>
      {relationship.partner_admin && formatPartnerAdminName(relationship.partner_admin)}
    </Table.Cell>
    <Table.Cell width={COLUMN_WIDTHS.role}>
      {isEditing
        ? (
          <PartnerAdminRelationshipRoleSelector
            currentRole={currentRole}
            onChange={(newRole) => {
              onRoleChange(relationship.id!, newRole)
              onEditEnd()
            }}
            onBlur={onEditEnd}
            hasExistingAssignee={hasAssignee}
            hideLabel
            disabled={isSaving}
          />
          )
        : (
          <div className='flex items-center gap-2'>
            <Badge color={ROLE_BADGE_COLORS[currentRole as keyof typeof ROLE_BADGE_COLORS]}>
              {capitalizeRole(currentRole)}
              {isPendingUpdate && ' *'}
            </Badge>
            <button
              type='button'
              onClick={() => onEditStart(relationship.id!)}
              className='inline-flex items-center justify-center text-indigo-600 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 rounded disabled:opacity-50'
              title='Change role'
              disabled={isSaving}
            >
              <PencilIcon className='h-4 w-4' />
            </button>
          </div>
          )}
    </Table.Cell>
    <Table.Cell width={COLUMN_WIDTHS.date}>
      {relationship.created_at && format(new Date(relationship.created_at), 'MMM d, yyyy')}
    </Table.Cell>
    <Table.ActionsCell>
      <IconButton
        icon={Trash2Icon}
        label='Remove'
        onClick={() => onDelete(relationship.id!)}
        variant='destructive'
        size='small'
        disabled={isSaving}
      />
    </Table.ActionsCell>
  </Table.Row>
)

interface PendingAddRowProps {
  pendingAdd: PendingAdd
  isSaving: boolean
  onDelete: (tempId: string) => void
}

const PendingAddRow = ({ pendingAdd, isSaving, onDelete }: PendingAddRowProps) => (
  <Table.Row key={pendingAdd.tempId}>
    <Table.Cell width={COLUMN_WIDTHS.name}>
      {formatPartnerAdminName(pendingAdd.partnerAdmin)}
    </Table.Cell>
    <Table.Cell width={COLUMN_WIDTHS.role}>
      <div className='flex items-center gap-2'>
        <Badge color={ROLE_BADGE_COLORS[pendingAdd.role as keyof typeof ROLE_BADGE_COLORS]}>
          {capitalizeRole(pendingAdd.role)} *
        </Badge>
      </div>
    </Table.Cell>
    <Table.Cell width={COLUMN_WIDTHS.date}>
      <span className='text-gray-400 italic'>Pending</span>
    </Table.Cell>
    <Table.ActionsCell>
      <IconButton
        icon={Trash2Icon}
        label='Remove'
        onClick={() => onDelete(pendingAdd.tempId)}
        variant='destructive'
        size='small'
        disabled={isSaving}
      />
    </Table.ActionsCell>
  </Table.Row>
)

interface PendingDeleteRowProps {
  relationship: WebAPIPartnersEntitiesPartnerAdminRelationship
  isSaving: boolean
  onUndo: (relationshipId: string) => void
}

const PendingDeleteRow = ({ relationship, isSaving, onUndo }: PendingDeleteRowProps) => (
  <Table.Row key={relationship.id}>
    <Table.Cell width={COLUMN_WIDTHS.name}>
      <span className='line-through'>
        {relationship.partner_admin && formatPartnerAdminName(relationship.partner_admin)}
      </span>
    </Table.Cell>
    <Table.Cell width={COLUMN_WIDTHS.role}>
      <Badge color='gray'>
        <span className='line-through'>
          {relationship.role && capitalizeRole(relationship.role)}
        </span>
      </Badge>
    </Table.Cell>
    <Table.Cell width={COLUMN_WIDTHS.date}>
      <span className='line-through'>
        {relationship.created_at && format(new Date(relationship.created_at), 'MMM d, yyyy')}
      </span>
    </Table.Cell>
    <Table.ActionsCell>
      <button
        type='button'
        onClick={() => onUndo(relationship.id!)}
        className='inline-flex items-center justify-center text-indigo-600 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 rounded disabled:opacity-50'
        title='Undo'
        disabled={isSaving}
      >
        <Undo2Icon className='h-4 w-4' />
      </button>
    </Table.ActionsCell>
  </Table.Row>
)

export const PartnerAdminRelationshipSheet = ({
  employerId,
  partnerId,
  partnerDisplayName,
  isOpen,
  onClose,
  onUpdate,
}: PartnerAdminRelationshipSheetProps) => {
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false)
  const [editingRelationshipId, setEditingRelationshipId] = useState<string | null>(null)

  // Track pending changes
  const [pendingAdds, setPendingAdds] = useState<PendingAdd[]>([])
  const [pendingUpdates, setPendingUpdates] = useState<Map<string, string>>(new Map())
  const [pendingDeletes, setPendingDeletes] = useState<Set<string>>(new Set())

  const queryClient = useQueryClient()

  // Reset pending changes when sheet closes or opens
  useEffect(() => {
    if (isOpen) {
      setPendingAdds([])
      setPendingUpdates(new Map())
      setPendingDeletes(new Set())
      setIsAddingNew(false)
      setEditingRelationshipId(null)
    }
  }, [isOpen])

  // Fetch relationships and available admins
  const { data, isLoading } = useGetPartnersPartnerIdPartnerAdminRelationships(
    partnerId,
    { prospect_employer_id: employerId },
    {
      query: {
        enabled: isOpen,
      },
    }
  )

  const relationships = data?.relationships ?? []
  const availablePartnerAdmins = data?.available_partner_admins ?? []

  // Batch mutation (used when saving all changes at once)
  const batchMutation = usePostPartnersPartnerIdPartnerAdminRelationshipsBatch()

  const hasUnsavedChanges = pendingAdds.length > 0 || pendingUpdates.size > 0 || pendingDeletes.size > 0

  const handleAdd = (partnerAdminId: string, role: string) => {
    const partnerAdmin = availablePartnerAdmins.find(pa => pa.id === partnerAdminId)
    if (!partnerAdmin) return

    const tempId = `temp-${Date.now()}-${Math.random()}`
    setPendingAdds([...pendingAdds, { tempId, partnerAdmin, role }])
    setIsAddingNew(false)
  }

  const handleRoleChange = (relationshipId: string, newRole: string) => {
    const newUpdates = new Map(pendingUpdates)
    newUpdates.set(relationshipId, newRole)
    setPendingUpdates(newUpdates)
  }

  const handleDelete = (relationshipId: string) => {
    const newDeletes = new Set(pendingDeletes)
    newDeletes.add(relationshipId)
    setPendingDeletes(newDeletes)
  }

  const handleDeletePendingAdd = (tempId: string) => {
    setPendingAdds(pendingAdds.filter(pa => pa.tempId !== tempId))
  }

  const handleUndoDelete = (relationshipId: string) => {
    const newDeletes = new Set(pendingDeletes)
    newDeletes.delete(relationshipId)
    setPendingDeletes(newDeletes)
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      // Build batch request
      const batchRequest: {
        prospect_employer_id: string,
        creates?: Array<{ partner_admin_id: string; role: string }>
        updates?: Array<{ id: string; role: string }>
        deletes?: string[]
      } = {
        prospect_employer_id: employerId,
      }

      if (pendingAdds.length > 0) {
        batchRequest.creates = pendingAdds.map(pa => ({
          partner_admin_id: pa.partnerAdmin.id!,
          role: pa.role,
        }))
      }

      if (pendingUpdates.size > 0) {
        batchRequest.updates = Array.from(pendingUpdates.entries()).map(([id, role]) => ({
          id,
          role,
        }))
      }

      if (pendingDeletes.size > 0) {
        batchRequest.deletes = Array.from(pendingDeletes)
      }

      // Execute batch operation
      await batchMutation.mutateAsync({
        partnerId,
        data: batchRequest as PostPartnersPartnerIdPartnerAdminRelationshipsBatch,
      })

      // Clear pending changes
      setPendingAdds([])
      setPendingUpdates(new Map())
      setPendingDeletes(new Set())

      // Invalidate queries and notify parent
      queryClient.invalidateQueries({
        queryKey: [
          getGetPartnersPartnerIdPartnerAdminRelationshipsQueryKey(
            partnerId,
            { prospect_employer_id: employerId }
          )[0],
        ],
      })

      toast.success('Changes saved successfully')
      onUpdate?.()
      onClose()
    } catch (error: any) {
      console.error('Failed to save changes:', error)
      const errorMessage = error?.response?.data?.message || 'Failed to save changes. Please try again.'
      toast.error(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesDialog(true)
    } else {
      onClose()
    }
  }

  const confirmCancel = () => {
    setPendingAdds([])
    setPendingUpdates(new Map())
    setPendingDeletes(new Set())
    setIsAddingNew(false)
    setEditingRelationshipId(null)
    setShowUnsavedChangesDialog(false)
    onClose()
  }

  // Get the current role for a relationship (considering pending updates)
  const getCurrentRole = (relationshipId: string, originalRole: string | undefined) => {
    return pendingUpdates.get(relationshipId) ?? originalRole ?? ''
  }

  // Check if there's an assignee in the current state (including pending changes)
  const hasAssigneeInCurrentState = () => {
    // Check existing relationships (not deleted, and either originally assignee or updated to assignee)
    const hasExistingAssignee = relationships.some(r =>
      !pendingDeletes.has(r.id!) && getCurrentRole(r.id!, r.role) === 'assignee'
    )

    // Check pending adds
    const hasPendingAssignee = pendingAdds.some(pa => pa.role === 'assignee')

    return hasExistingAssignee || hasPendingAssignee
  }

  // Get available partner admins (excluding those being added)
  const getAvailablePartnerAdmins = () => {
    const pendingAddIds = new Set(pendingAdds.map(pa => pa.partnerAdmin.id!))
    return availablePartnerAdmins.filter(pa => !pendingAddIds.has(pa.id!))
  }

  const availableForAdd = getAvailablePartnerAdmins()
  const hasAssignee = hasAssigneeInCurrentState()
  const hasNoRelationships = relationships.length === 0 && pendingAdds.length === 0 && !isAddingNew
  const hasRelationships = relationships.length > 0 || pendingAdds.length > 0

  return (
    <>
      <Sheet open={isOpen} onOpenChange={handleCancel}>
        <SheetContent className='sm:max-w-2xl overflow-y-auto flex flex-col'>
          <SheetHeader>
            <SheetTitle>
              Manage Partner Admins for {partnerDisplayName}
            </SheetTitle>
            <SheetDescription>
              Add, edit, or remove partner admin access for this employer.
            </SheetDescription>
          </SheetHeader>

          <div className='mt-6 flex-1 overflow-y-auto'>
            {isLoading
              ? (
                <div className='text-center py-8 text-gray-500'>Loading...</div>
                )
              : (
                <>
                  <RolePermissionsInfo />

                  <Table width='full'>
                    <Table.Head>
                      <Table.Row>
                        <Table.ColumnHeader>Partner Admin</Table.ColumnHeader>
                        <Table.ColumnHeader>Role</Table.ColumnHeader>
                        <Table.ColumnHeader>Added</Table.ColumnHeader>
                        <Table.ActionColumnHeader />
                      </Table.Row>
                    </Table.Head>
                    <Table.Body>
                      {hasNoRelationships
                        ? (
                          <EmptyState
                            availableAdminsCount={availableForAdd.length}
                            onAddClick={() => setIsAddingNew(true)}
                            isSaving={isSaving}
                          />
                          )
                        : (
                          <>
                            {/* Existing relationships */}
                            {relationships
                              .filter(r => !pendingDeletes.has(r.id!))
                              .map((relationship) => {
                                const currentRole = getCurrentRole(relationship.id!, relationship.role)
                                const isPendingUpdate = pendingUpdates.has(relationship.id!)
                                const isEditing = editingRelationshipId === relationship.id

                                return (
                                  <ExistingRelationshipRow
                                    key={relationship.id}
                                    relationship={relationship}
                                    currentRole={currentRole}
                                    isPendingUpdate={isPendingUpdate}
                                    isEditing={isEditing}
                                    hasAssignee={hasAssignee}
                                    isSaving={isSaving}
                                    onRoleChange={handleRoleChange}
                                    onDelete={handleDelete}
                                    onEditStart={setEditingRelationshipId}
                                    onEditEnd={() => setEditingRelationshipId(null)}
                                  />
                                )
                              })}

                            {/* Pending adds */}
                            {pendingAdds.map((pendingAdd) => (
                              <PendingAddRow
                                key={pendingAdd.tempId}
                                pendingAdd={pendingAdd}
                                isSaving={isSaving}
                                onDelete={handleDeletePendingAdd}
                              />
                            ))}

                            {/* Deleted rows shown with strikethrough */}
                            {relationships
                              .filter(r => pendingDeletes.has(r.id!))
                              .map((relationship) => (
                                <PendingDeleteRow
                                  key={relationship.id}
                                  relationship={relationship}
                                  isSaving={isSaving}
                                  onUndo={handleUndoDelete}
                                />
                              ))}
                          </>
                          )}
                      {isAddingNew && (
                        <AddPartnerAdminRelationshipRow
                          availablePartnerAdmins={availableForAdd}
                          onAdd={handleAdd}
                          onCancel={() => setIsAddingNew(false)}
                          hasExistingAssignee={hasAssignee}
                          isSubmitting={false}
                        />
                      )}
                    </Table.Body>
                  </Table>

                  {!isAddingNew && availableForAdd.length > 0 && hasRelationships && (
                    <div className='mt-4 mr-4 flex justify-end'>
                      <IconButton
                        icon={PlusIcon}
                        label='Add Partner Admin'
                        onClick={() => setIsAddingNew(true)}
                        variant='secondary'
                        size='medium'
                        disabled={isSaving}
                      />
                    </div>
                  )}

                  {!isAddingNew && availableForAdd.length === 0 && hasRelationships && (
                    <div className='mt-4 text-sm text-gray-500'>
                      All partner admins from this partner have been assigned
                    </div>
                  )}
                </>
                )}
          </div>

          {/* Save/Cancel buttons at the bottom */}
          {!isLoading && (
            <div className='mt-6 pt-4 border-t border-gray-200 flex justify-end gap-2'>
              <Button
                variant='secondary'
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={handleSave}
                disabled={!hasUnsavedChanges || isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <ConfirmationDialog
        open={showUnsavedChangesDialog}
        onOpenChange={(open) => setShowUnsavedChangesDialog(open)}
        onSubmit={confirmCancel}
        title='Discard unsaved changes?'
        description='You have unsaved changes. Are you sure you want to close without saving?'
        submitLabel='Discard'
        isDanger
      />
    </>
  )
}
